import { useState, useContext, useEffect } from "react"
import { css } from "@emotion/core"
import RegisterableContext from "components/admin/registerable/context"
import { StackView, Button } from "@planningcenter/tapestry-react"
import { Pane, StatusMessage } from "components/ui"
import SelectionGroup from "components/admin/registerable/SelectionGroup"
import SignupTimeSelections from "components/admin/registerable/SignupTimeSelections"
import SignupTimeAttendeeSelections from "./SignupTimeAttendeeSelections"
import AttendeeSelections from "./AttendeeSelections"

const Selections = () => {
  const { registerable } = useContext(RegisterableContext)
  const [status, setStatus] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [selections, setSelections] = useState(registerable.attendeeTypeSelections)

  useEffect(() => {
    setSelections(registerable.attendeeTypeSelections)
  }, [JSON.stringify(registerable.attendeeTypeSelections.map(selection => selection.count))])

  const withSaveStatus = promise => {
    setStatus({ text: "Saving...", icon: "spinner" })
    setIsSaving(true)
    promise().finally(() => {
      setIsSaving(false)
      setStatus(null)
    })
  }

  const handleSave = () => {
    withSaveStatus(() =>
      registerable
        .update({
          attendee_type_selections_attributes: selections.map(({ id, count }) => ({
            id,
            count
          }))
        })
        .then(() => {
          setIsEditing(false)
        })
    )
  }

  const handleCancel = () => {
    setSelections(registerable.attendeeTypeSelections)
    setIsEditing(false)
  }

  const handleChange = selectionChanges => {
    setSelections(
      selections.map(selection => {
        const changes = selectionChanges.find(change => change.id == selection.id) || {}
        return { ...selection, ...changes }
      })
    )
  }

  const showTimes = selections.some(({ signupTimeId }) => !!signupTimeId)

  return (
    <Pane marginBottom={1}>
      <Pane.Header>
        <Pane.Title>Registration #{registerable.id}</Pane.Title>
        <StackView axis="horizontal" alignment="center" spacing={1}>
          {status && (
            <div className="mr-1">
              <StatusMessage {...status} />
            </div>
          )}
          {isEditing ? (
            <>
              <Button
                onClick={handleCancel}
                title="Cancel"
                variant="outline"
                size="xs"
                disabled={isSaving}
              />
              <Button
                onClick={handleSave}
                title="Save"
                theme="create"
                size="xs"
                disabled={isSaving}
              />
            </>
          ) : (
            <Button title="Edit" variant="outline" size="xs" onClick={() => setIsEditing(true)} />
          )}
        </StackView>
      </Pane.Header>
      {isEditing ? (
        <Pane.Body axis="vertical" paddingVertical={0} css={selectionsStyles}>
          {showTimes ? (
            <div className="editRegistrationWithSplitTimes">
              <SignupTimeSelections onSelectionsChange={handleChange} selections={selections} />
            </div>
          ) : (
            <div className="editRegistrationWithNoSplitTimes">
              <SelectionGroup onSelectionsChange={handleChange} selections={selections} />
            </div>
          )}
        </Pane.Body>
      ) : (
        <Pane.Body axis="vertical" paddingVertical={0} css={selectionsStyles}>
          {showTimes ? (
            <div className="showRegistrationWithSplitTimes">
              <SignupTimeAttendeeSelections withSaveStatus={withSaveStatus} />
            </div>
          ) : (
            <div className="showRegistrationWithNoSplitTimes">
              <AttendeeSelections withSaveStatus={withSaveStatus} />
            </div>
          )}
          {registerable.totalCostMinusDiscounts && (
            <dl className="d-f jc-sb d-f@print jc-sb@print mb-2">
              <dt>
                <strong>Total</strong>
              </dt>
              <dd>
                <strong>{registerable.totalCostMinusDiscounts}</strong>
              </dd>
            </dl>
          )}
        </Pane.Body>
      )}
    </Pane>
  )
}

const selectionsStyles = css`
  & .showRegistrationWithSplitTimes .attendeeSelection {
    margin-bottom: 1rem;
  }
  & .showRegistrationWithNoSplitTimes .attendeeSelection {
    margin-bottom: 1rem;
    margin-top: 1rem;
  }
  & .showRegistrationWithNoSplitTimes .attendeeSelection:last-child:not(:only-child) {
    margin-top: 0;
  }

  & .editRegistrationWithSplitTimes .signupTime {
    margin-top: 1rem;
  }
  & .editRegistrationWithSplitTimes .signupTimeChecked {
    margin-bottom: 1rem;

    & label {
      font-weight: 500;
    }
  }
  & .editRegistrationWithSplitTimes .signupTimeWrapper:last-child .signupTime {
    margin-bottom: 1rem;
  }
  & .editRegistrationWithSplitTimes .selectionList {
    margin-bottom: 1rem;
  }
  & .editRegistrationWithNoSplitTimes .selectionList {
    margin-bottom: 1rem;
    margin-top: 1rem;
  }
`

export default Selections
